import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { getWithAuth } from "../../api/api";
import { API_URLS } from "../../api/apiConfig";
import dayjs from "dayjs";

const ModalBlockHistory = ({ onClose, block_id }) => {
  // --------------------------------------------
  // Campos a ocultar (configúralos aquí mismo)
  // --------------------------------------------
  const HIDDEN_FIELDS_FORM = [
    // "external_id",
    // "room_id",
  ];
  const HIDDEN_FIELDS_CHANGES = ["receptions_fields"];
  // --------------------------------------------

  const [records, setRecords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const current = records[currentIndex] || {};
  const block = current.new_document || {};

  const hideForm = new Set(HIDDEN_FIELDS_FORM);
  const hideChanges = new Set(HIDDEN_FIELDS_CHANGES);

  // Etiquetas legibles
  const FIELD_LABELS = {
    start: "Fecha de inicio",
    end: "Fecha de fin",
    functional_status: "Estado",
    block_type: "Tipo de bloqueo",
    is_active: "¿Activo?",
    external_id: "ID externo",
    booking_external_id: "ID de Reserva",
    room_id: "ID de habitación",
  };

  // Campos visibles en el formulario (en orden)
  const FORM_FIELDS = [
    "start",
    "end",
    "functional_status",
    "block_type",
    "is_active",
    "external_id",
  ];

  // helpers
  const formatTimestamp = (t) => dayjs(t).format("DD/MM/YYYY HH:mm");

  const getActionLabel = (action) => {
    switch (action) {
      case "create":
        return "Creación";
      case "update":
        return "Actualización";
      case "delete":
        return "Eliminación";
      case "read":
        return "Consulta";
      default:
        return "Desconocida";
    }
  };

  const formatDateInput = (dateStr) => dateStr?.split("T")[0] || "-";

  const formatValue = (value) => {
    if (typeof value === "boolean") return value ? "Sí" : "No";
    if (value == null) return "—";
    return String(value);
  };

  // Soporta {from,to} y {old_value,new_value}
  const getPrevNext = (changeObj) => {
    if (!changeObj || typeof changeObj !== "object")
      return { prev: "—", next: "—" };
    const prev =
      "from" in changeObj
        ? changeObj.from
        : "old_value" in changeObj
        ? changeObj.old_value
        : undefined;
    const next =
      "to" in changeObj
        ? changeObj.to
        : "new_value" in changeObj
        ? changeObj.new_value
        : undefined;
    return { prev, next };
  };

  const getFormFieldValue = (field) => {
    const v = block?.[field];
    if (field === "start" || field === "end") return formatDateInput(v);
    if (field === "is_active") return v ? "Sí" : "No";
    return v ?? "";
  };

  const getFormFieldType = (field) => {
    if (field === "start" || field === "end") return "date";
    return "text";
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getWithAuth(API_URLS.GET_BLOCK_HISTORY(block_id));
        if (res.message === "success") {
          setRecords(res.data || []);
        }
      } catch (error) {
        console.error("Error al obtener el historial del bloqueo", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [block_id]);

  return (
    <Modal show={true} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Historial de Bloqueo{" "}
          {records.length > 0
            ? `Versión ${currentIndex + 1} de ${records.length}`
            : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center">No hay historial disponible.</div>
        ) : (
          <div className="modal-body py-5 px-lg-17">
            {/* Meta */}
            <div className="mb-4">
              <p>
                <strong>Acción:</strong> {getActionLabel(current.action)}
              </p>
              <p>
                <strong>Fecha:</strong> {formatTimestamp(current.timestamp)}
              </p>
              <p>
                <strong>Modificado por:</strong>{" "}
                {current.performed_by?.email || "Desconocido"}
              </p>
            </div>

            {/* Documento (solo lectura) */}
            <Form>
              {FORM_FIELDS.filter((f) => !hideForm.has(f)).map((field) => (
                <Form.Group className="mb-3" key={field}>
                  <Form.Label>{FIELD_LABELS[field] || field}</Form.Label>
                  <Form.Control
                    type={getFormFieldType(field)}
                    readOnly
                    value={getFormFieldValue(field)}
                  />
                </Form.Group>
              ))}
            </Form>

            {/* Cambios */}
            {current.changes && (
              <div className="mt-5">
                <h5 className="mb-3">Cambios realizados:</h5>
                {Object.entries(current.changes)
                  .filter(([field]) => !hideChanges.has(field))
                  .map(([field, change]) => {
                    const { prev, next } = getPrevNext(change);

                    const maybeFormat = (f, v) => {
                      if (f === "start" || f === "end")
                        return formatDateInput(v);
                      if (f === "is_active") return formatValue(Boolean(v));
                      return formatValue(v);
                    };

                    return (
                      <div
                        key={field}
                        className="mb-4 ps-3 border-start border-3 border-warning"
                      >
                        <div className="fw-bold text-capitalize">
                          {FIELD_LABELS[field] || field.replace(/_/g, " ")}
                        </div>
                        <div className="text-muted ms-2">
                          <div>
                            <strong>Anterior:</strong>{" "}
                            {maybeFormat(field, prev)}
                          </div>
                          <div>
                            <strong>Actual:</strong> {maybeFormat(field, next)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {records.length > 0 && (
        <Modal.Footer className="justify-content-between">
          <Button
            variant="secondary"
            onClick={() => setCurrentIndex(Math.max(currentIndex - 1, 0))}
            disabled={currentIndex === 0}
          >
            Anterior
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              setCurrentIndex(Math.min(currentIndex + 1, records.length - 1))
            }
            disabled={currentIndex === records.length - 1}
          >
            Siguiente
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ModalBlockHistory;
